// /scada/control — power control (R1, full). Per-station limit setpoint with
// Select-Before-Operate confirm + simulated command lifecycle
// (pending → sent → verified | timeout), manual/auto mode, limit removal.
// DEMO: actual write to inverters is disabled; confirming updates local state only.
import { useEffect, useRef, useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard, StatusBadge, SboConfirmModal } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useFleetTelemetry } from '@/scada/useTelemetry';
import { useAllowedStationIds } from '@/scada/useScopedStations';

const cfgOf = (id: string) => STATIONS.find((s) => s.id === id)!;
const nameOf = (id: string) => cfgOf(id)?.name ?? id;

type CmdStatus = 'idle' | 'pending' | 'sent' | 'verified' | 'timeout';
interface Cmd { status: CmdStatus; pct: number }

const CMD_STYLE: Record<CmdStatus, { c: string; label: string }> = {
  idle: { c: scada.textMuted, label: '—' },
  pending: { c: scada.state.warning, label: 'Перевірка…' },
  sent: { c: scada.state.curtailed, label: 'Надіслано…' },
  verified: { c: scada.state.ok, label: 'Виконано ✓' },
  timeout: { c: scada.state.alarm, label: 'Немає підтвердж.' },
};

export default function ScadaControlPage() {
  const { data: rawData } = useFleetTelemetry();
  const allowed = useAllowedStationIds();
  const data = allowed ? rawData.filter((d) => allowed.has(d.stationId)) : rawData;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [override, setOverride] = useState<Record<string, number>>({});
  const [mode, setMode] = useState<Record<string, 'manual' | 'auto'>>({});
  const [cmd, setCmd] = useState<Record<string, Cmd>>({});
  const [confirm, setConfirm] = useState<{ id: string; pct: number; remove?: boolean } | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const addLog = (msg: string) =>
    setLog((l) => [`${new Date().toLocaleTimeString('uk-UA')} — ${msg}`, ...l].slice(0, 10));

  const curPct = (id: string): number => {
    if (override[id] != null) return override[id];
    const d = data.find((x) => x.stationId === id);
    const cap = cfgOf(id).acCapacityKw;
    return d?.activeLimitKw != null ? Math.round((d.activeLimitKw / cap) * 100) : 100;
  };

  const runLifecycle = (id: string, pct: number, remove?: boolean) => {
    setCmd((c) => ({ ...c, [id]: { status: 'pending', pct } }));
    addLog(`${nameOf(id)}: запит ${remove ? 'зняття ліміту (100%)' : `ліміту ${pct}%`} — перевірка`);
    const t1 = setTimeout(() => {
      setCmd((c) => ({ ...c, [id]: { status: 'sent', pct } }));
      addLog(`${nameOf(id)}: команду надіслано (демонстрація — без запису в обладнання)`);
    }, 1500);
    const t2 = setTimeout(() => {
      // ~85% verified, else timeout — to show both branches
      const ok = (pct % 7) !== 3;
      if (ok) {
        setCmd((c) => ({ ...c, [id]: { status: 'verified', pct } }));
        setOverride((o) => ({ ...o, [id]: pct }));
        addLog(`${nameOf(id)}: підтверджено фактичну зміну → ${pct}%`);
      } else {
        setCmd((c) => ({ ...c, [id]: { status: 'timeout', pct } }));
        addLog(`${nameOf(id)}: НЕМАЄ підтвердження виконання (timeout)`);
      }
    }, 3200);
    timers.current.push(t1, t2);
  };

  const doConfirm = () => {
    if (!confirm) return;
    runLifecycle(confirm.id, confirm.pct, confirm.remove);
    setDraft((p) => ({ ...p, [confirm.id]: '' }));
    setConfirm(null);
  };

  const target = confirm ? data.find((d) => d.stationId === confirm.id) : null;

  const selStyle = { background: scada.bg, border: `1px solid ${scada.border}`, color: scada.text, borderRadius: 6, padding: '5px 8px', fontSize: 13 } as const;

  return (
    <ScadaLayout active="control">
      <div style={{ background: `${scada.state.warning}14`, border: `1px solid ${scada.state.warning}40`, color: scada.state.warning, borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 16 }}>
        <i className="ri-information-line" style={{ marginRight: 6 }} />
        Демо режим: команди у вигляді превью проходять перевірку, фіксуються в журналі — пряме керування обладнанням вимкнено.
      </div>

      <SectionCard title="Керування потужністю станцій" subtitle="Єдиний екран з підтвердженням та контролем виконання кожної дії" padded={false}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Станція', 'Стан', 'P факт', 'Поточний ліміт', 'Режим', 'Новий ліміт, %', 'Дія', 'Виконання'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.filter((d) => d.state !== 'commissioning').map((d) => {
                const id = d.stationId;
                const cp = curPct(id);
                const m = mode[id] ?? (d.controlMode === 'auto' ? 'auto' : 'manual');
                const st = cmd[id]?.status ?? 'idle';
                const limited = cp < 100;
                const disabled = d.state === 'offline';
                return (
                  <tr key={id} style={{ borderTop: `1px solid ${scada.border}` }}>
                    <td style={{ padding: '10px 12px', color: scada.text, fontSize: 13, fontWeight: 600 }}>{nameOf(id)}</td>
                    <td style={{ padding: '10px 12px' }}><StatusBadge state={d.state} small /></td>
                    <td style={{ padding: '10px 12px', color: scada.text, fontSize: 13 }}>{(d.activePowerKw / 1000).toFixed(2)} МВт</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: limited ? scada.state.curtailed : scada.textMuted, fontSize: 13, fontWeight: limited ? 600 : 400 }}>{cp}%</span>
                      {d.limitReason === 'operational_safety' && <span style={{ color: scada.state.alarm, fontSize: 10, display: 'block' }}>опер. безпека</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button onClick={() => setMode((p) => ({ ...p, [id]: m === 'manual' ? 'auto' : 'manual' }))}
                        disabled={disabled}
                        style={{ background: m === 'auto' ? `${scada.state.curtailed}1A` : scada.panel2, color: m === 'auto' ? scada.state.curtailed : scada.textDim, border: `1px solid ${m === 'auto' ? scada.state.curtailed + '60' : scada.border}`, borderRadius: 999, padding: '3px 10px', fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                        {m === 'auto' ? 'Авто' : 'Ручний'}
                      </button>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <input type="number" min={0} max={100} placeholder={String(cp)}
                        value={draft[id] ?? ''}
                        onChange={(e) => setDraft((p) => ({ ...p, [id]: e.target.value }))}
                        disabled={disabled || m === 'auto'}
                        style={{ ...selStyle, width: 80, opacity: m === 'auto' ? 0.4 : 1 }} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div className="flex gap-1">
                        <button
                          disabled={disabled || m === 'auto' || !draft[id]}
                          onClick={() => setConfirm({ id, pct: Number(draft[id]) })}
                          style={{ background: disabled || m === 'auto' || !draft[id] ? scada.panel2 : scada.accent, color: disabled || m === 'auto' || !draft[id] ? scada.textMuted : '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: disabled || m === 'auto' || !draft[id] ? 'not-allowed' : 'pointer' }}>
                          Задати
                        </button>
                        {limited && (
                          <button disabled={disabled || m === 'auto'}
                            onClick={() => setConfirm({ id, pct: 100, remove: true })}
                            title="Зняти обмеження (повернути 100%)"
                            style={{ background: 'transparent', color: scada.textDim, border: `1px solid ${scada.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: disabled || m === 'auto' ? 'not-allowed' : 'pointer' }}>
                            Зняти
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: CMD_STYLE[st].c, fontSize: 12, fontWeight: st === 'verified' || st === 'timeout' ? 600 : 400 }}>
                        {CMD_STYLE[st].label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {log.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <SectionCard title="Журнал дій (демонстрація)">
            {log.map((l, i) => (
              <div key={i} style={{ color: scada.textDim, fontSize: 12, padding: '3px 0', borderBottom: i < log.length - 1 ? `1px solid ${scada.border}` : 'none' }}>{l}</div>
            ))}
          </SectionCard>
        </div>
      )}

      <SboConfirmModal
        open={!!confirm}
        title={confirm?.remove ? 'Підтвердження зняття обмеження' : 'Підтвердження зміни ліміту потужності'}
        current={confirm ? curPct(confirm.id) : 0}
        next={confirm?.pct ?? 0}
        unit="%"
        warning={
          confirm && confirm.pct > curPct(confirm.id) && target?.limitReason === 'operational_safety'
            ? 'Активне обмеження операційної безпеки має вищий пріоритет — підвищення буде відхилено до його зняття.'
            : 'Демонстраційний режим: фактичне керування обладнанням поки вимкнено.'
        }
        onConfirm={doConfirm}
        onCancel={() => setConfirm(null)}
      />
    </ScadaLayout>
  );
}
